import { ref, watch, onMounted, unref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { signIn, confirmSignIn, signOut } from 'aws-amplify/auth'
import { formatLoginErrorMessage } from '@/utils/loginErrors'

const OTP_CHALLENGE_STEPS = [
  'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE',
  'CONFIRM_SIGN_IN_WITH_EMAIL_CODE',
  'CONFIRM_SIGN_IN_WITH_SMS_CODE',
]

const LOGIN_SUCCESS_DELAY_MS = 2200

export function useLoginAuth({ otpControls, onOtpReady, formRef }) {
  const router = useRouter()
  const route = useRoute()
  const store = useStore()

  const email = ref('')
  const isLoading = ref(false)
  const loginCompleting = ref(false)
  const user = ref(null)

  const loginBusy = () => isLoading.value || loginCompleting.value

  watch(email, (val) => {
    if (val) email.value = val.toLowerCase()
  })

  const emailRules = [
    (v) => !!v || 'Email is required',
    (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Enter valid email',
  ]

  function maskedEmail() {
    const value = email.value
    if (!value || !value.includes('@')) return value
    const [local, domain] = value.split('@')
    return `${local.slice(0, 2)}•••@${domain}`
  }

  function requiresOtpChallenge(nextStep) {
    return OTP_CHALLENGE_STEPS.includes(nextStep?.signInStep)
  }

  function navigateAfterLogin() {
    const redirect = route.query.redirect
    setTimeout(() => {
      store.commit('SET_AUTHENTICATED', true)
      if (typeof redirect === 'string' && redirect) {
        router.push(redirect)
        return
      }
      router.push({ name: 'Dashboard' })
    }, LOGIN_SUCCESS_DELAY_MS)
  }

  function beginLoginCompletion() {
    loginCompleting.value = true
    otpControls.stopCounter()
  }

  async function sendOtpRequest() {
    if (loginBusy()) return false

    if (!email.value) return false

    if (!otpControls.otpSent.value) {
      const form = unref(formRef?.value)
      if (!form?.validate) return false
      const { valid } = await form.validate()
      if (!valid) return false
    }

    return signInMethod('SEND_OTP')
  }

  async function verifyOtpRequest() {
    if (loginBusy()) return false
    if (!otpControls.isOtpComplete.value) return false
    return signInMethod('VERIFY_OTP')
  }

  async function signInMethod(action) {
    switch (action) {
      case 'SEND_OTP':
        try {
          isLoading.value = true
          user.value = await signIn({
            username: email.value,
            options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
          })

          if (user.value.isSignedIn) {
            beginLoginCompletion()
            navigateAfterLogin()
            return { ok: true, message: 'Login successful', type: 'success' }
          }

          if (requiresOtpChallenge(user.value.nextStep)) {
            otpControls.beginOtpStep()
            onOtpReady?.()
            return {
              ok: true,
              type: 'success',
              snackbar: {
                title: 'Verification code sent to',
                subtitle: email.value,
              },
            }
          }

          return {
            ok: false,
            message: 'Unexpected sign-in step. Please contact support.',
            type: 'error',
          }
        } catch (error) {
          if (error.message === 'There is already a signed in user.') {
            await signOut({ global: true })
            return signInMethod('SEND_OTP')
          }
          return {
            ok: false,
            message: formatLoginErrorMessage(error),
            type: 'error',
          }
        } finally {
          if (!loginCompleting.value) {
            isLoading.value = false
          }
        }

      case 'VERIFY_OTP':
        try {
          isLoading.value = true
          const output = await confirmSignIn({
            challengeResponse: otpControls.otpValue.value,
          })

          if (output.isSignedIn) {
            beginLoginCompletion()
            navigateAfterLogin()
            return { ok: true, message: 'Login successful', type: 'success' }
          }

          if (requiresOtpChallenge(output.nextStep)) {
            return {
              ok: false,
              message: 'Additional verification required.',
              type: 'error',
            }
          }

          return {
            ok: false,
            message: 'Could not verify the code. Please try again.',
            type: 'error',
          }
        } catch (err) {
          if (
            err.message?.includes('Incorrect OTP') ||
            err.message === 'Invalid session for the user.'
          ) {
            otpControls.counter.value = 0
            otpControls.otpDigits.value = ['', '', '', '', '', '']
          }
          return {
            ok: false,
            message: formatLoginErrorMessage(err, 'Invalid or expired code.'),
            type: 'error',
          }
        } finally {
          if (!loginCompleting.value) {
            isLoading.value = false
          }
        }

      default:
        return { ok: false, message: 'Unknown action', type: 'error' }
    }
  }

  async function resetForm() {
    if (loginCompleting.value) return
    otpControls.resetOtp()
    user.value = null

    try {
      await signOut({ global: false })
    } catch {
      /* no active session */
    }

    setTimeout(() => {
      const form = unref(formRef?.value)
      form?.reset?.()
      document.getElementById('login-email')?.focus()
    }, 0)
  }

  function disableHistoryChanges() {
    window.history.pushState({ ...history.state }, '', window.location.href)
    window.onpopstate = () => {
      window.history.pushState(
        { ...history.state },
        '',
        window.location.href,
      )
    }
  }

  onMounted(() => {
    disableHistoryChanges()
  })

  return {
    email,
    isLoading,
    loginCompleting,
    emailRules,
    loginBusy,
    maskedEmail,
    sendOtpRequest,
    verifyOtpRequest,
    resetForm,
  }
}
